import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Svg,
  Path,
} from "@react-pdf/renderer";

const A4 = { width: "210mm", height: "297mm" };

// Fixed inner card frame — identical across every design so that anyone
// printing & trimming the bundle gets uniform cuts regardless of which design
// was picked. Sits inside the page padding below.
const PAGE_PADDING_V = "40mm";
const PAGE_PADDING_H = "38mm";
const CARD_W = "134mm"; // = 210 - 2*38
const CARD_H = "217mm"; // = 297 - 2*40

const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "").padEnd(6, "0").slice(0, 6);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

const darken = (hex, factor = 0.7) => {
  const { r, g, b } = hexToRgb(hex);
  const f = (n) => Math.max(0, Math.min(255, Math.round(n * factor)))
    .toString(16)
    .padStart(2, "0");
  return `#${f(r)}${f(g)}${f(b)}`;
};

const pickBannerText = (hex, hint) => {
  if ((hint || "").toLowerCase() === "light") return "#1a1a1a";
  if ((hint || "").toLowerCase() === "dark") return "#ffffff";
  const { r, g, b } = hexToRgb(hex);
  const lin = (c) => {
    const cc = c / 255;
    return cc <= 0.04045 ? cc / 12.92 : Math.pow((cc + 0.055) / 1.055, 2.4);
  };
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return lum > 0.4 ? "#1a1a1a" : "#ffffff";
};

const tint = (hex, alpha = 0.12) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
};

const hasFeatures = (p) =>
  [p.bedrooms, p.bathrooms, p.carspaces].some(
    (v) => v != null && v !== "" && String(v) !== "0" && Number(v) > 0
  );

const streetLine = (p) => {
  const head = p.flat_number
    ? `${p.flat_number}/${p.street_number || ""}`
    : p.street_number || "";
  return [head, p.street_name, p.street_type_long || p.street_type]
    .filter(Boolean)
    .join(" ")
    .trim();
};

const suburbLine = (p) =>
  [p.suburb, p.state, p.postcode].filter(Boolean).join(" ").trim();

// ── icons ─────────────────────────────────────────────────────────────
const IconBolt = ({ color, size = 9 }) => (
  <Svg viewBox="0 0 320 512" style={{ width: size, height: size }}>
    <Path
      fill={color}
      d="M296 160H180.6l42.6-129.8C227.2 16.5 215.7 0 200.9 0H56C44.2 0 34.2 8.6 32.4 20.3L.6 220.3C-1.6 234.6 9.4 248 24 248h118.7L96.6 482.5C93.8 496.6 105.4 512 119.9 512c8 0 15.7-4 20.2-11.1l175.2-272c11.6-18-1.1-41.9-19.3-68.9z"
    />
  </Svg>
);

const IconCheck = ({ color, size = 9 }) => (
  <Svg viewBox="0 0 512 512" style={{ width: size, height: size }}>
    <Path
      fill={color}
      d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm141.4 184.6l-150 168c-4.5 5-10.9 7.9-17.6 7.9-6.4 0-12.5-2.5-17-7.1l-90-90c-9.4-9.4-9.4-24.6 0-33.9 9.4-9.4 24.6-9.4 33.9 0l71.7 71.7 133-148.9c8.8-9.8 23.9-10.7 33.7-1.9 9.9 8.8 10.7 23.9 1.9 33.7z"
    />
  </Svg>
);

const IconLock = ({ color, size = 9 }) => (
  <Svg viewBox="0 0 448 512" style={{ width: size, height: size }}>
    <Path
      fill={color}
      d="M400 224h-24v-72C376 68.2 307.8 0 224 0S72 68.2 72 152v72H48c-26.5 0-48 21.5-48 48v192c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V272c0-26.5-21.5-48-48-48zm-104 0H152v-72c0-39.7 32.3-72 72-72s72 32.3 72 72v72z"
    />
  </Svg>
);

const IconPin = ({ color, size = 9 }) => (
  <Svg viewBox="0 0 384 512" style={{ width: size, height: size * (12 / 9) }}>
    <Path
      fill={color}
      d="M172.3 501.7C27 291 0 269.4 0 192 0 86 86 0 192 0s192 86 192 192c0 77.4-27 99-172.3 309.7-9.5 13.7-29.9 13.7-39.4 0z"
    />
  </Svg>
);

// ── shared design metadata (exported for picker UI) ───────────────────
export const PROPERTY_QR_DESIGNS = [
  {
    id: "classic",
    name: "Classic",
    description: "The original framed card — agency banner, hero photo, chips, QR, agent footer.",
  },
  {
    id: "bold",
    name: "Bold & Large",
    description: "Same structure as Classic, scaled up — bigger hero, bigger QR, heavier type.",
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Spare layout — large QR is the hero, only address & agent details surround it.",
  },
  {
    id: "magazine",
    name: "Magazine",
    description: "Editorial split — full-bleed hero on top, two-column lower with details & QR.",
  },
  {
    id: "postcard",
    name: "Postcard",
    description: "QR-first — coloured top band, centered QR, photo strip & agent block below.",
  },
  {
    id: "trifold",
    name: "Trifold",
    description: "Designed to fold A4 into thirds — QR sits in the middle panel, safely away from both creases.",
  },
];

export const DEFAULT_PROPERTY_QR_COPY = {
  agencyNameShort: "",
  headline: "See what this home is really worth, instantly",
  chip1: "Instant estimate",
  chip2: "100% free",
  chip3: "No sign-up",
  chip4: "Local insights",
  scanPrompt: "Scan with your phone camera",
  inPersonHeadline: "Prefer an in-person price update?",
  inPersonCta: "Call {name} on {phone}",
  noImageMode: "dark", // "dark" = white text on agency colour, "light" = dark text on white
};

const noImgPalette = (mode, colourDark, bannerText) =>
  (mode || "").toLowerCase() === "light"
    ? { bg: "#ffffff", fg: "#111827", accent: "#374151" }
    : { bg: colourDark, fg: bannerText, accent: bannerText };

// ── classic design (original) ─────────────────────────────────────────
const classicStyles = (colour, colourDark, bannerText, ni) =>
  StyleSheet.create({
    page: {
      width: A4.width,
      height: A4.height,
      paddingTop: PAGE_PADDING_V,
      paddingBottom: PAGE_PADDING_V,
      paddingLeft: PAGE_PADDING_H,
      paddingRight: PAGE_PADDING_H,
      backgroundColor: "#ffffff",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    card: {
      width: CARD_W,
      height: CARD_H,
      flexDirection: "column",
      overflow: "hidden",
      borderWidth: 12,
      borderColor: "#d1d5db",
    },
    agencyBar: {
      backgroundColor: colour,
      color: bannerText,
      paddingTop: "5mm",
      paddingBottom: "5mm",
      paddingLeft: "8mm",
      paddingRight: "8mm",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    logo: { maxHeight: "10mm", maxWidth: "50mm", objectFit: "contain" },
    agencyName: { color: bannerText, fontSize: 11, fontWeight: 700, letterSpacing: 0.3 },
    heroWrap: { width: "100%", height: "55mm", position: "relative" },
    hero: { width: "100%", height: "55mm", objectFit: "cover" },
    heroNoImg: {
      width: "100%", height: "55mm", padding: "8mm",
      backgroundColor: ni.bg, color: ni.fg,
      alignItems: "center", justifyContent: "center",
    },
    heroOverlay: {
      position: "absolute", left: "4mm", right: "4mm", bottom: "4mm",
      flexDirection: "column", alignItems: "center",
      paddingTop: 5, paddingBottom: 5, paddingLeft: 9, paddingRight: 9,
      backgroundColor: "rgba(0,0,0,0.62)", borderRadius: 6,
    },
    heroOverlayAddress: {
      color: "#ffffff", fontSize: 11, fontWeight: 700,
      letterSpacing: 0.2, marginBottom: 3, textAlign: "center",
    },
    heroOverlayFeatures: { flexDirection: "row", alignItems: "center" },
    heroOverlayText: {
      color: "#ffffff", fontSize: 9, fontWeight: 700,
      marginRight: 5, letterSpacing: 0.3,
    },
    propertyType: {
      color: ni.fg, fontSize: 8.5, letterSpacing: 2,
      opacity: 0.85, marginBottom: 4, textTransform: "uppercase",
    },
    fullAddress: { color: ni.fg, fontSize: 13, fontWeight: 700, textAlign: "center", lineHeight: 1.25 },
    noImgFeatures: { flexDirection: "row", justifyContent: "center", alignItems: "center", marginTop: 6 },
    noImgFeatureText: {
      color: ni.fg, fontSize: 10, fontWeight: 700,
      marginHorizontal: 6, letterSpacing: 0.3, opacity: 0.95,
    },
    summary: { paddingTop: "4mm", paddingLeft: "8mm", paddingRight: "8mm", fontSize: 9, color: "#374151", lineHeight: 1.5 },
    cta: { flex: 1, paddingTop: "5mm", paddingBottom: "4mm", paddingLeft: "8mm", paddingRight: "8mm", alignItems: "center", justifyContent: "center" },
    scanHeadline: { fontSize: 12, fontWeight: 700, color: "#111827", textAlign: "center", marginBottom: "4mm" },
    trustRow: { flexDirection: "row", justifyContent: "center", marginBottom: "3mm", gap: 4 },
    chip: {
      flexDirection: "row", alignItems: "center",
      paddingTop: 3, paddingBottom: 3, paddingLeft: 7, paddingRight: 7,
      borderRadius: 12, backgroundColor: tint(colour, 0.12), marginHorizontal: 2,
    },
    chipText: { fontSize: 7.5, fontWeight: 700, color: colourDark, marginLeft: 3, letterSpacing: 0.3 },
    qrWrap: {
      width: "42mm", height: "42mm", padding: 5, backgroundColor: "#ffffff",
      borderWidth: 0.6, borderColor: "#e5e7eb", borderRadius: 4,
      alignItems: "center", justifyContent: "center",
      flexShrink: 0, flexGrow: 0,
    },
    qr: { width: "100%", height: "100%", objectFit: "contain" },
    scanPrompt: {
      marginTop: "3mm", fontSize: 8.5, color: colour,
      fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase",
    },
    inPersonNote: { marginTop: "2mm", fontSize: 8.5, color: "#4b5563", textAlign: "center", maxWidth: "100mm", lineHeight: 1.4 },
    agent: {
      flexDirection: "row", alignItems: "center",
      paddingTop: "4mm", paddingBottom: "5mm", paddingLeft: "8mm", paddingRight: "8mm",
      borderTop: "0.5pt solid #e5e7eb", backgroundColor: "#fafafa",
    },
    avatar: {
      width: "16mm", height: "16mm", borderRadius: 23,
      marginRight: "4mm", objectFit: "cover", backgroundColor: "#e5e7eb",
    },
    name: { fontSize: 11, fontWeight: 700, color: "#111827" },
    title: { fontSize: 8, color: "#6b7280", marginTop: 1 },
    contactRow: { flexDirection: "row", marginTop: 2 },
    contactLabel: { color: colour, fontWeight: 700, fontSize: 8.5, width: "11mm" },
    contactValue: { color: "#374151", fontSize: 8.5 },
  });

function ClassicPage({ p, agent, c, colour, colourDark, bannerText, inPersonCta, agentFontFamily }) {
  const ni = noImgPalette(c.noImageMode, colourDark, bannerText);
  const styles = classicStyles(colour, colourDark, bannerText, ni);
  const street = streetLine(p);
  const sub = suburbLine(p);
  const showFeatures = hasFeatures(p);
  return (
    <Page size="A4" style={agentFontFamily ? [styles.page, { fontFamily: agentFontFamily }] : styles.page}>
      <View style={styles.card}>
        <View style={styles.agencyBar}>
          {agent.logo_url ? <Image src={agent.logo_url} style={styles.logo} /> : <View />}
          <Text style={styles.agencyName}>{c.agencyNameShort || agent.agency_name}</Text>
        </View>

        {p.photo_url_full ? (
          <View style={styles.heroWrap}>
            <Image src={p.photo_url_full} style={styles.hero} />
            <View style={styles.heroOverlay}>
              <Text style={styles.heroOverlayAddress}>{[street, sub].filter(Boolean).join(", ")}</Text>
              {showFeatures && (
                <View style={styles.heroOverlayFeatures}>
                  <Text style={styles.heroOverlayText}>{p.bedrooms || 0} Bed</Text>
                  <Text style={styles.heroOverlayText}>·</Text>
                  <Text style={styles.heroOverlayText}>{p.bathrooms || 0} Bath</Text>
                  <Text style={styles.heroOverlayText}>·</Text>
                  <Text style={styles.heroOverlayText}>{p.carspaces || 0} Car</Text>
                </View>
              )}
            </View>
          </View>
        ) : (
          <View style={styles.heroNoImg}>
            <Text style={styles.propertyType}>{p.listing_property_type || "Property"}</Text>
            <Text style={styles.fullAddress}>{street}</Text>
            <Text style={styles.fullAddress}>{sub}</Text>
            {showFeatures && (
              <View style={styles.noImgFeatures}>
                <Text style={styles.noImgFeatureText}>{p.bedrooms || 0} Bed</Text>
                <Text style={styles.noImgFeatureText}>·</Text>
                <Text style={styles.noImgFeatureText}>{p.bathrooms || 0} Bath</Text>
                <Text style={styles.noImgFeatureText}>·</Text>
                <Text style={styles.noImgFeatureText}>{p.carspaces || 0} Car</Text>
              </View>
            )}
          </View>
        )}

        {p.ai_summary ? <Text style={styles.summary}>{p.ai_summary}</Text> : null}

        <View style={styles.cta}>
          <Text style={styles.scanHeadline}>{c.headline}</Text>
          <View style={styles.trustRow}>
            <View style={styles.chip}><IconBolt color={colourDark} /><Text style={styles.chipText}>{c.chip1}</Text></View>
            <View style={styles.chip}><IconCheck color={colourDark} /><Text style={styles.chipText}>{c.chip2}</Text></View>
            <View style={styles.chip}><IconLock color={colourDark} /><Text style={styles.chipText}>{c.chip3}</Text></View>
            <View style={styles.chip}><IconPin color={colourDark} /><Text style={styles.chipText}>{c.chip4}</Text></View>
          </View>
          <View style={styles.qrWrap}>{p.qr_data_url && <Image src={p.qr_data_url} style={styles.qr} />}</View>
          <Text style={styles.scanPrompt}>{c.scanPrompt}</Text>
          {agent.agent_phone ? (
            <Text style={styles.inPersonNote}>{c.inPersonHeadline}{"\n"}{inPersonCta}</Text>
          ) : null}
        </View>

        <View style={styles.agent}>
          {agent.profile_image_url ? <Image src={agent.profile_image_url} style={styles.avatar} /> : <View style={styles.avatar} />}
          <View style={{ flexShrink: 1 }}>
            <Text style={styles.name}>{agent.agent_name}</Text>
            <Text style={styles.title}>{agent.agent_title}</Text>
            {agent.agent_phone ? (
              <View style={styles.contactRow}>
                <Text style={styles.contactLabel}>Call</Text>
                <Text style={styles.contactValue}>{agent.agent_phone}</Text>
              </View>
            ) : null}
            {agent.agent_email ? (
              <View style={styles.contactRow}>
                <Text style={styles.contactLabel}>Email</Text>
                <Text style={styles.contactValue}>{agent.agent_email}</Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </Page>
  );
}

// ── bold design (scaled-up classic) ───────────────────────────────────
const boldStyles = (colour, colourDark, bannerText, ni) =>
  StyleSheet.create({
    page: {
      width: A4.width, height: A4.height,
      paddingTop: PAGE_PADDING_V, paddingBottom: PAGE_PADDING_V,
      paddingLeft: PAGE_PADDING_H, paddingRight: PAGE_PADDING_H,
      backgroundColor: "#ffffff",
      flexDirection: "column", alignItems: "center", justifyContent: "center",
    },
    card: {
      width: CARD_W, height: CARD_H,
      flexDirection: "column", overflow: "hidden",
      borderWidth: 16, borderColor: colour,
    },
    agencyBar: {
      backgroundColor: colour, color: bannerText,
      paddingTop: "7mm", paddingBottom: "7mm", paddingLeft: "9mm", paddingRight: "9mm",
      flexDirection: "row", alignItems: "center", justifyContent: "space-between",
    },
    logo: { maxHeight: "13mm", maxWidth: "55mm", objectFit: "contain" },
    agencyName: { color: bannerText, fontSize: 14, fontWeight: 700, letterSpacing: 0.5 },
    heroWrap: { width: "100%", height: "80mm", position: "relative" },
    hero: { width: "100%", height: "80mm", objectFit: "cover" },
    heroNoImg: {
      width: "100%", height: "80mm", padding: "10mm",
      backgroundColor: ni.bg, color: ni.fg,
      alignItems: "center", justifyContent: "center",
    },
    heroOverlay: {
      position: "absolute", left: "5mm", right: "5mm", bottom: "5mm",
      flexDirection: "column", alignItems: "center",
      paddingTop: 7, paddingBottom: 7, paddingLeft: 12, paddingRight: 12,
      backgroundColor: "rgba(0,0,0,0.65)", borderRadius: 8,
    },
    heroOverlayAddress: { color: "#fff", fontSize: 15, fontWeight: 700, marginBottom: 4, textAlign: "center" },
    heroOverlayFeatures: { flexDirection: "row" },
    heroOverlayText: { color: "#fff", fontSize: 11, fontWeight: 700, marginRight: 6, letterSpacing: 0.3 },
    propertyType: { color: ni.fg, fontSize: 11, letterSpacing: 2.5, opacity: 0.85, marginBottom: 6, textTransform: "uppercase" },
    fullAddress: { color: ni.fg, fontSize: 18, fontWeight: 700, textAlign: "center", lineHeight: 1.25 },
    noImgFeatures: { flexDirection: "row", justifyContent: "center", marginTop: 8 },
    noImgFeatureText: { color: ni.fg, fontSize: 13, fontWeight: 700, marginHorizontal: 8 },
    cta: { flex: 1, paddingTop: "7mm", paddingBottom: "5mm", paddingLeft: "9mm", paddingRight: "9mm", alignItems: "center", justifyContent: "center" },
    scanHeadline: { fontSize: 17, fontWeight: 700, color: "#111827", textAlign: "center", marginBottom: "5mm", lineHeight: 1.25 },
    qrWrap: {
      width: "60mm", height: "60mm", padding: 6, backgroundColor: "#fff",
      borderWidth: 1, borderColor: colour, borderRadius: 6,
      alignItems: "center", justifyContent: "center",
      flexShrink: 0, flexGrow: 0,
    },
    qr: { width: "100%", height: "100%", objectFit: "contain" },
    scanPrompt: { marginTop: "4mm", fontSize: 11, color: colour, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" },
    inPersonNote: { marginTop: "3mm", fontSize: 10, color: "#4b5563", textAlign: "center", lineHeight: 1.4 },
    agent: {
      flexDirection: "row", alignItems: "center",
      paddingTop: "5mm", paddingBottom: "6mm", paddingLeft: "9mm", paddingRight: "9mm",
      borderTop: "0.5pt solid #e5e7eb", backgroundColor: "#fafafa",
    },
    avatar: { width: "20mm", height: "20mm", borderRadius: 28, marginRight: "5mm", objectFit: "cover", backgroundColor: "#e5e7eb" },
    name: { fontSize: 14, fontWeight: 700, color: "#111827" },
    title: { fontSize: 10, color: "#6b7280", marginTop: 1 },
    contactRow: { flexDirection: "row", marginTop: 3 },
    contactLabel: { color: colour, fontWeight: 700, fontSize: 10.5, width: "14mm" },
    contactValue: { color: "#374151", fontSize: 10.5 },
  });

function BoldPage({ p, agent, c, colour, colourDark, bannerText, inPersonCta, agentFontFamily }) {
  const ni = noImgPalette(c.noImageMode, colourDark, bannerText);
  const styles = boldStyles(colour, colourDark, bannerText, ni);
  const street = streetLine(p);
  const sub = suburbLine(p);
  const showFeatures = hasFeatures(p);
  return (
    <Page size="A4" style={agentFontFamily ? [styles.page, { fontFamily: agentFontFamily }] : styles.page}>
      <View style={styles.card}>
        <View style={styles.agencyBar}>
          {agent.logo_url ? <Image src={agent.logo_url} style={styles.logo} /> : <View />}
          <Text style={styles.agencyName}>{c.agencyNameShort || agent.agency_name}</Text>
        </View>
        {p.photo_url_full ? (
          <View style={styles.heroWrap}>
            <Image src={p.photo_url_full} style={styles.hero} />
            <View style={styles.heroOverlay}>
              <Text style={styles.heroOverlayAddress}>{[street, sub].filter(Boolean).join(", ")}</Text>
              {showFeatures && (
                <View style={styles.heroOverlayFeatures}>
                  <Text style={styles.heroOverlayText}>{p.bedrooms || 0} Bed</Text>
                  <Text style={styles.heroOverlayText}>·</Text>
                  <Text style={styles.heroOverlayText}>{p.bathrooms || 0} Bath</Text>
                  <Text style={styles.heroOverlayText}>·</Text>
                  <Text style={styles.heroOverlayText}>{p.carspaces || 0} Car</Text>
                </View>
              )}
            </View>
          </View>
        ) : (
          <View style={styles.heroNoImg}>
            <Text style={styles.propertyType}>{p.listing_property_type || "Property"}</Text>
            <Text style={styles.fullAddress}>{street}</Text>
            <Text style={styles.fullAddress}>{sub}</Text>
            {showFeatures && (
              <View style={styles.noImgFeatures}>
                <Text style={styles.noImgFeatureText}>{p.bedrooms || 0} Bed</Text>
                <Text style={styles.noImgFeatureText}>·</Text>
                <Text style={styles.noImgFeatureText}>{p.bathrooms || 0} Bath</Text>
                <Text style={styles.noImgFeatureText}>·</Text>
                <Text style={styles.noImgFeatureText}>{p.carspaces || 0} Car</Text>
              </View>
            )}
          </View>
        )}
        <View style={styles.cta}>
          <Text style={styles.scanHeadline}>{c.headline}</Text>
          <View style={styles.qrWrap}>{p.qr_data_url && <Image src={p.qr_data_url} style={styles.qr} />}</View>
          <Text style={styles.scanPrompt}>{c.scanPrompt}</Text>
          {agent.agent_phone ? (
            <Text style={styles.inPersonNote}>{c.inPersonHeadline}{"\n"}{inPersonCta}</Text>
          ) : null}
        </View>
        <View style={styles.agent}>
          {agent.profile_image_url ? <Image src={agent.profile_image_url} style={styles.avatar} /> : <View style={styles.avatar} />}
          <View style={{ flexShrink: 1 }}>
            <Text style={styles.name}>{agent.agent_name}</Text>
            <Text style={styles.title}>{agent.agent_title}</Text>
            {agent.agent_phone ? (
              <View style={styles.contactRow}>
                <Text style={styles.contactLabel}>Call</Text>
                <Text style={styles.contactValue}>{agent.agent_phone}</Text>
              </View>
            ) : null}
            {agent.agent_email ? (
              <View style={styles.contactRow}>
                <Text style={styles.contactLabel}>Email</Text>
                <Text style={styles.contactValue}>{agent.agent_email}</Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </Page>
  );
}

// ── minimal design ────────────────────────────────────────────────────
const minimalStyles = (colour, colourDark) =>
  StyleSheet.create({
    page: {
      width: A4.width, height: A4.height,
      paddingTop: PAGE_PADDING_V, paddingBottom: PAGE_PADDING_V,
      paddingLeft: PAGE_PADDING_H, paddingRight: PAGE_PADDING_H,
      backgroundColor: "#ffffff",
      flexDirection: "column", alignItems: "center", justifyContent: "center",
    },
    card: {
      width: CARD_W, height: CARD_H,
      flexDirection: "column", alignItems: "center", justifyContent: "space-between",
      paddingTop: "16mm", paddingBottom: "14mm", paddingLeft: "10mm", paddingRight: "10mm",
      borderTop: `4pt solid ${colour}`, borderBottom: `4pt solid ${colour}`,
    },
    top: { alignItems: "center" },
    eyebrow: { fontSize: 9, color: colour, letterSpacing: 3, fontWeight: 700, textTransform: "uppercase", marginBottom: "5mm" },
    street: { fontSize: 22, fontWeight: 700, color: "#0f172a", textAlign: "center", lineHeight: 1.15 },
    suburb: { fontSize: 11, color: "#64748b", marginTop: 6, letterSpacing: 1.5, textTransform: "uppercase", textAlign: "center" },
    features: { flexDirection: "row", marginTop: "6mm", justifyContent: "center" },
    feat: { fontSize: 11, color: "#334155", fontWeight: 600, marginHorizontal: 6, letterSpacing: 0.5 },
    middle: { alignItems: "center" },
    headline: { fontSize: 13, color: "#334155", textAlign: "center", marginBottom: "6mm", lineHeight: 1.4, maxWidth: "100mm" },
    qrWrap: {
      width: "75mm", height: "75mm", padding: 4, backgroundColor: "#fff",
      alignItems: "center", justifyContent: "center",
    },
    qr: { width: "100%", height: "100%" },
    scanPrompt: { marginTop: "5mm", fontSize: 10, color: colour, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" },
    bottom: { alignItems: "center" },
    agentName: { fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 2 },
    agentLine: { fontSize: 10, color: "#64748b", marginTop: 1 },
    agency: { fontSize: 9, color: colourDark, marginTop: 6, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700 },
  });

function MinimalPage({ p, agent, c, colour, colourDark, inPersonCta, agentFontFamily }) {
  const styles = minimalStyles(colour, colourDark);
  const street = streetLine(p);
  const sub = suburbLine(p);
  const showFeatures = hasFeatures(p);
  return (
    <Page size="A4" style={agentFontFamily ? [styles.page, { fontFamily: agentFontFamily }] : styles.page}>
      <View style={styles.card}>
        <View style={styles.top}>
          <Text style={styles.eyebrow}>{p.listing_property_type || "Property"}</Text>
          <Text style={styles.street}>{street}</Text>
          <Text style={styles.suburb}>{sub}</Text>
          {showFeatures && (
            <View style={styles.features}>
              <Text style={styles.feat}>{p.bedrooms || 0} Bed</Text>
              <Text style={styles.feat}>·</Text>
              <Text style={styles.feat}>{p.bathrooms || 0} Bath</Text>
              <Text style={styles.feat}>·</Text>
              <Text style={styles.feat}>{p.carspaces || 0} Car</Text>
            </View>
          )}
        </View>

        <View style={styles.middle}>
          <Text style={styles.headline}>{c.headline}</Text>
          <View style={styles.qrWrap}>{p.qr_data_url && <Image src={p.qr_data_url} style={styles.qr} />}</View>
          <Text style={styles.scanPrompt}>{c.scanPrompt}</Text>
        </View>

        <View style={styles.bottom}>
          <Text style={styles.agentName}>{agent.agent_name}</Text>
          {agent.agent_title ? <Text style={styles.agentLine}>{agent.agent_title}</Text> : null}
          {agent.agent_phone ? <Text style={styles.agentLine}>{inPersonCta}</Text> : null}
          {agent.agent_email ? <Text style={styles.agentLine}>{agent.agent_email}</Text> : null}
          <Text style={styles.agency}>{c.agencyNameShort || agent.agency_name}</Text>
        </View>
      </View>
    </Page>
  );
}

// ── magazine design ───────────────────────────────────────────────────
const magazineStyles = (colour, colourDark, bannerText) =>
  StyleSheet.create({
    page: {
      width: A4.width, height: A4.height,
      paddingTop: PAGE_PADDING_V, paddingBottom: PAGE_PADDING_V,
      paddingLeft: PAGE_PADDING_H, paddingRight: PAGE_PADDING_H,
      backgroundColor: "#ffffff",
      flexDirection: "column", alignItems: "center", justifyContent: "center",
    },
    card: { width: CARD_W, height: CARD_H, flexDirection: "column", overflow: "hidden", borderWidth: 0.75, borderColor: "#111827" },
    heroWrap: { width: "100%", height: "110mm", position: "relative", backgroundColor: colourDark },
    hero: { width: "100%", height: "110mm", objectFit: "cover" },
    heroBadge: {
      position: "absolute", top: "5mm", left: "5mm",
      paddingTop: 4, paddingBottom: 4, paddingLeft: 9, paddingRight: 9,
      backgroundColor: colour, color: bannerText, borderRadius: 0,
    },
    heroBadgeText: { color: bannerText, fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" },
    addressBlock: {
      position: "absolute", left: "5mm", right: "5mm", bottom: "5mm",
      paddingTop: 7, paddingBottom: 7, paddingLeft: 11, paddingRight: 11,
      backgroundColor: "rgba(0,0,0,0.62)", borderRadius: 6,
    },
    addressStreet: { color: "#fff", fontSize: 16, fontWeight: 700, lineHeight: 1.15, textAlign: "center" },
    addressSub: { color: "#fff", fontSize: 10, marginTop: 3, letterSpacing: 0.4, opacity: 0.95, textAlign: "center" },
    addressFeat: { color: "#fff", fontSize: 10, marginTop: 4, opacity: 0.95, letterSpacing: 0.3, fontWeight: 700, textAlign: "center" },
    body: { flexDirection: "row", flex: 1 },
    bodyLeft: { flex: 1, padding: "7mm", borderRight: "0.5pt solid #e5e7eb", justifyContent: "space-between" },
    headline: { fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 },
    summary: { fontSize: 9, color: "#475569", lineHeight: 1.55, marginTop: "4mm" },
    chips: { flexDirection: "row", flexWrap: "wrap", marginTop: "4mm" },
    chip: { flexDirection: "row", alignItems: "center", marginBottom: 4, marginRight: 6 },
    chipText: { fontSize: 8.5, color: colourDark, fontWeight: 700, marginLeft: 3 },
    agentBlock: { marginTop: "5mm", paddingTop: "4mm", borderTop: "0.5pt solid #e5e7eb", flexDirection: "row", alignItems: "center" },
    avatar: { width: "13mm", height: "13mm", borderRadius: 18, marginRight: "3mm", objectFit: "cover", backgroundColor: "#e5e7eb" },
    agentName: { fontSize: 10.5, fontWeight: 700, color: "#0f172a" },
    agentLine: { fontSize: 8.5, color: "#475569", marginTop: 1 },
    bodyRight: { width: "62mm", padding: "7mm", alignItems: "center", justifyContent: "center", backgroundColor: "#f8fafc" },
    qrWrap: { width: "48mm", height: "48mm", padding: 4, backgroundColor: "#fff", borderWidth: 0.6, borderColor: "#e5e7eb" },
    qr: { width: "100%", height: "100%" },
    scanPrompt: { marginTop: "4mm", fontSize: 9, color: colour, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", textAlign: "center" },
    scanSub: { marginTop: "2mm", fontSize: 8.5, color: "#64748b", textAlign: "center", lineHeight: 1.35 },
    agency: { fontSize: 8, color: "#0f172a", letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700, marginTop: "5mm" },
  });

function MagazinePage({ p, agent, c, colour, colourDark, bannerText, inPersonCta, agentFontFamily }) {
  const styles = magazineStyles(colour, colourDark, bannerText);
  const street = streetLine(p);
  const sub = suburbLine(p);
  const showFeatures = hasFeatures(p);
  const hasPhoto = !!p.photo_url_full;
  const ni = noImgPalette(c.noImageMode, colourDark, bannerText);
  return (
    <Page size="A4" style={agentFontFamily ? [styles.page, { fontFamily: agentFontFamily }] : styles.page}>
      <View style={styles.card}>
        {hasPhoto ? (
          <View style={styles.heroWrap}>
            <Image src={p.photo_url_full} style={styles.hero} />
            <View style={styles.heroBadge}>
              <Text style={styles.heroBadgeText}>{p.listing_property_type || "Property"}</Text>
            </View>
            <View style={styles.addressBlock}>
              <Text style={styles.addressStreet}>{street}</Text>
              <Text style={styles.addressSub}>{sub}</Text>
              {showFeatures && (
                <Text style={styles.addressFeat}>
                  {p.bedrooms || 0} Bed · {p.bathrooms || 0} Bath · {p.carspaces || 0} Car
                </Text>
              )}
            </View>
          </View>
        ) : (
          <View
            style={[
              styles.heroWrap,
              { backgroundColor: ni.bg, alignItems: "center", justifyContent: "center", padding: "12mm" },
            ]}
          >
            <Text style={{ color: ni.fg, fontSize: 9, letterSpacing: 2.5, fontWeight: 700, textTransform: "uppercase", marginBottom: 8, opacity: 0.85 }}>
              {p.listing_property_type || "Property"}
            </Text>
            <Text style={{ color: ni.fg, fontSize: 19, fontWeight: 700, textAlign: "center", lineHeight: 1.2 }}>
              {street}
            </Text>
            <Text style={{ color: ni.fg, fontSize: 11, marginTop: 4, letterSpacing: 0.8, textAlign: "center", opacity: 0.9 }}>
              {sub}
            </Text>
            {showFeatures && (
              <Text style={{ color: ni.fg, fontSize: 11, marginTop: 10, fontWeight: 700, letterSpacing: 0.5 }}>
                {p.bedrooms || 0} Bed · {p.bathrooms || 0} Bath · {p.carspaces || 0} Car
              </Text>
            )}
          </View>
        )}
        <View style={styles.body}>
          <View style={styles.bodyLeft}>
            <View>
              <Text style={styles.headline}>{c.headline}</Text>
              {p.ai_summary ? <Text style={styles.summary}>{p.ai_summary}</Text> : null}
              <View style={styles.chips}>
                <View style={styles.chip}><IconBolt color={colourDark} /><Text style={styles.chipText}>{c.chip1}</Text></View>
                <View style={styles.chip}><IconCheck color={colourDark} /><Text style={styles.chipText}>{c.chip2}</Text></View>
                <View style={styles.chip}><IconLock color={colourDark} /><Text style={styles.chipText}>{c.chip3}</Text></View>
                <View style={styles.chip}><IconPin color={colourDark} /><Text style={styles.chipText}>{c.chip4}</Text></View>
              </View>
            </View>
            <View style={styles.agentBlock}>
              {agent.profile_image_url ? <Image src={agent.profile_image_url} style={styles.avatar} /> : <View style={styles.avatar} />}
              <View style={{ flexShrink: 1 }}>
                <Text style={styles.agentName}>{agent.agent_name}</Text>
                {agent.agent_phone ? <Text style={styles.agentLine}>{agent.agent_phone}</Text> : null}
                {agent.agent_email ? <Text style={styles.agentLine}>{agent.agent_email}</Text> : null}
              </View>
            </View>
          </View>
          <View style={styles.bodyRight}>
            <View style={styles.qrWrap}>{p.qr_data_url && <Image src={p.qr_data_url} style={styles.qr} />}</View>
            <Text style={styles.scanPrompt}>{c.scanPrompt}</Text>
            {agent.agent_phone ? <Text style={styles.scanSub}>{c.inPersonHeadline}{"\n"}{inPersonCta}</Text> : null}
            <Text style={styles.agency}>{c.agencyNameShort || agent.agency_name}</Text>
          </View>
        </View>
      </View>
    </Page>
  );
}

// ── postcard design ───────────────────────────────────────────────────
const postcardStyles = (colour, colourDark, bannerText) =>
  StyleSheet.create({
    page: {
      width: A4.width, height: A4.height,
      paddingTop: PAGE_PADDING_V, paddingBottom: PAGE_PADDING_V,
      paddingLeft: PAGE_PADDING_H, paddingRight: PAGE_PADDING_H,
      backgroundColor: "#ffffff",
      flexDirection: "column", alignItems: "center", justifyContent: "center",
    },
    card: { width: CARD_W, height: CARD_H, flexDirection: "column", overflow: "hidden", backgroundColor: "#fff", borderWidth: 1, borderColor: colourDark },
    band: { backgroundColor: colour, paddingTop: "8mm", paddingBottom: "8mm", paddingLeft: "8mm", paddingRight: "8mm", alignItems: "center" },
    bandAgency: { color: bannerText, fontSize: 11, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 6 },
    bandHeadline: { color: bannerText, fontSize: 16, fontWeight: 700, textAlign: "center", lineHeight: 1.25, maxWidth: "110mm" },
    qrSection: { alignItems: "center", paddingTop: "9mm", paddingBottom: "6mm" },
    qrWrap: {
      width: "70mm", height: "70mm", padding: 5, backgroundColor: "#fff",
      borderWidth: 4, borderColor: colour, alignItems: "center", justifyContent: "center",
    },
    qr: { width: "100%", height: "100%" },
    scanPrompt: { marginTop: "4mm", fontSize: 11, color: colourDark, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" },
    address: {
      marginTop: "4mm", paddingTop: "3mm", paddingLeft: "6mm", paddingRight: "6mm",
      alignItems: "center", borderTop: `0.5pt solid ${tint(colour, 0.4)}`,
    },
    street: { fontSize: 13, fontWeight: 700, color: "#0f172a", textAlign: "center" },
    suburb: { fontSize: 10, color: "#64748b", marginTop: 2, letterSpacing: 1, textTransform: "uppercase" },
    feat: { fontSize: 10, color: "#334155", marginTop: 4 },
    photoStrip: { width: "100%", height: "38mm", backgroundColor: colourDark, marginTop: "auto" },
    photo: { width: "100%", height: "38mm", objectFit: "cover" },
    agentRow: {
      flexDirection: "row", alignItems: "center",
      paddingTop: "5mm", paddingBottom: "6mm", paddingLeft: "8mm", paddingRight: "8mm",
      backgroundColor: "#0f172a", marginTop: "auto",
    },
    avatar: { width: "15mm", height: "15mm", borderRadius: 21, marginRight: "4mm", objectFit: "cover", backgroundColor: "#334155" },
    agentName: { fontSize: 12, fontWeight: 700, color: "#fff" },
    agentLine: { fontSize: 9, color: "#cbd5e1", marginTop: 1 },
  });

function PostcardPage({ p, agent, c, colour, colourDark, bannerText, inPersonCta, agentFontFamily }) {
  const styles = postcardStyles(colour, colourDark, bannerText);
  const hasPhoto = !!p.photo_url_full;
  const street = streetLine(p);
  const sub = suburbLine(p);
  const showFeatures = hasFeatures(p);
  return (
    <Page size="A4" style={agentFontFamily ? [styles.page, { fontFamily: agentFontFamily }] : styles.page}>
      <View style={styles.card}>
        <View style={styles.band}>
          <Text style={styles.bandAgency}>{c.agencyNameShort || agent.agency_name}</Text>
          <Text style={styles.bandHeadline}>{c.headline}</Text>
        </View>
        <View style={styles.qrSection}>
          <View style={styles.qrWrap}>{p.qr_data_url && <Image src={p.qr_data_url} style={styles.qr} />}</View>
          <Text style={styles.scanPrompt}>{c.scanPrompt}</Text>
        </View>
        <View style={styles.address}>
          <Text style={styles.street}>{street}</Text>
          <Text style={styles.suburb}>{sub}</Text>
          {showFeatures && (
            <Text style={styles.feat}>{p.bedrooms || 0} Bed · {p.bathrooms || 0} Bath · {p.carspaces || 0} Car</Text>
          )}
        </View>
        {hasPhoto ? (
          <View style={styles.photoStrip}>
            <Image src={p.photo_url_full} style={styles.photo} />
          </View>
        ) : null}
        <View style={styles.agentRow}>
          {agent.profile_image_url ? <Image src={agent.profile_image_url} style={styles.avatar} /> : <View style={styles.avatar} />}
          <View style={{ flexShrink: 1 }}>
            <Text style={styles.agentName}>{agent.agent_name}</Text>
            {agent.agent_title ? <Text style={styles.agentLine}>{agent.agent_title}</Text> : null}
            {agent.agent_phone ? <Text style={styles.agentLine}>{inPersonCta}</Text> : null}
            {agent.agent_email ? <Text style={styles.agentLine}>{agent.agent_email}</Text> : null}
          </View>
        </View>
      </View>
    </Page>
  );
}

// ── trifold design ────────────────────────────────────────────────────
// Trifold is folded, not cut, so it ignores the shared cut frame and
// pushes nearly to the page edges. A4 portrait folds at y = 99mm and
// y = 198mm. With a slim 6mm page margin the card starts at y = 6mm, so
// the creases land 93mm and 192mm into the card → three panels of
// 93 / 99 / 93 mm. The QR lives centred inside the middle 99mm panel,
// comfortably clear of either fold.
const TRIFOLD_PAD = "6mm";
const TRIFOLD_CARD_W = "198mm"; // 210 - 2*6
const TRIFOLD_CARD_H = "285mm"; // 297 - 2*6
const PANEL_TOP_H = "93mm";
const PANEL_MID_H = "99mm";
const PANEL_BOT_H = "93mm";

const trifoldStyles = (colour, colourDark, bannerText, ni) =>
  StyleSheet.create({
    page: {
      width: A4.width, height: A4.height,
      paddingTop: TRIFOLD_PAD, paddingBottom: TRIFOLD_PAD,
      paddingLeft: TRIFOLD_PAD, paddingRight: TRIFOLD_PAD,
      backgroundColor: "#ffffff",
      flexDirection: "column", alignItems: "center", justifyContent: "center",
    },
    card: { width: TRIFOLD_CARD_W, height: TRIFOLD_CARD_H, flexDirection: "column", overflow: "hidden", borderWidth: 0.75, borderColor: "#cbd5e1" },

    // Top panel (front face after folding) — agency bar + hero photo
    panelTop: { height: PANEL_TOP_H, flexDirection: "column", overflow: "hidden" },
    agencyBar: {
      backgroundColor: colour, paddingTop: "3mm", paddingBottom: "3mm",
      paddingLeft: "8mm", paddingRight: "8mm",
      flexDirection: "row", alignItems: "center", justifyContent: "space-between",
    },
    logo: { maxHeight: "8mm", maxWidth: "45mm", objectFit: "contain" },
    agencyName: { color: bannerText, fontSize: 10, fontWeight: 700, letterSpacing: 0.4 },
    hero: { width: "100%", flex: 1, objectFit: "cover" },
    heroNoImg: { width: "100%", flex: 1, padding: "6mm", backgroundColor: ni.bg, alignItems: "center", justifyContent: "center" },
    noImgEyebrow: { color: ni.fg, fontSize: 8.5, letterSpacing: 2, fontWeight: 700, textTransform: "uppercase", marginBottom: 5, opacity: 0.85 },
    noImgStreet: { color: ni.fg, fontSize: 14, fontWeight: 700, textAlign: "center", lineHeight: 1.2 },
    noImgSub: { color: ni.fg, fontSize: 9.5, marginTop: 3, letterSpacing: 0.6, textAlign: "center", opacity: 0.9 },

    // Fold guides — subtle dashed line + tiny scissors hint at the margins.
    foldGuide: {
      height: 0.5, backgroundColor: "transparent",
      borderTopWidth: 0.5, borderTopColor: "#cbd5e1", borderStyle: "dashed",
    },

    // Middle panel — address strip + QR centered + scan prompt
    panelMid: {
      height: PANEL_MID_H, flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: "6mm 8mm", backgroundColor: "#ffffff",
    },
    addressStrip: { alignItems: "center", marginBottom: "4mm" },
    street: { fontSize: 12, fontWeight: 700, color: "#0f172a", textAlign: "center" },
    suburb: { fontSize: 9, color: "#64748b", marginTop: 2, letterSpacing: 0.8, textTransform: "uppercase" },
    feat: { fontSize: 9, color: "#334155", marginTop: 3, fontWeight: 600 },
    qrWrap: {
      width: "60mm", height: "60mm", padding: 5, backgroundColor: "#ffffff",
      borderWidth: 1, borderColor: colour, alignItems: "center", justifyContent: "center",
    },
    qr: { width: "100%", height: "100%" },
    scanPrompt: { marginTop: "3mm", fontSize: 9, color: colour, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" },
    headline: { fontSize: 10, color: "#334155", textAlign: "center", marginTop: "2mm", lineHeight: 1.4, maxWidth: "95mm" },

    // Bottom panel — agent row
    panelBot: {
      height: PANEL_BOT_H, flexDirection: "row", alignItems: "center",
      paddingLeft: "8mm", paddingRight: "8mm",
      borderTop: `0.5pt solid ${tint(colour, 0.4)}`, backgroundColor: "#fafafa",
    },
    avatar: { width: "18mm", height: "18mm", borderRadius: 26, marginRight: "5mm", objectFit: "cover", backgroundColor: "#e5e7eb" },
    name: { fontSize: 12, fontWeight: 700, color: "#111827" },
    title: { fontSize: 9, color: "#6b7280", marginTop: 1 },
    contactRow: { flexDirection: "row", marginTop: 2 },
    contactLabel: { color: colour, fontWeight: 700, fontSize: 9, width: "12mm" },
    contactValue: { color: "#374151", fontSize: 9 },
  });

function TrifoldPage({ p, agent, c, colour, colourDark, bannerText, inPersonCta, agentFontFamily }) {
  const ni = noImgPalette(c.noImageMode, colourDark, bannerText);
  const styles = trifoldStyles(colour, colourDark, bannerText, ni);
  const street = streetLine(p);
  const sub = suburbLine(p);
  const showFeatures = hasFeatures(p);
  return (
    <Page size="A4" style={agentFontFamily ? [styles.page, { fontFamily: agentFontFamily }] : styles.page}>
      <View style={styles.card}>
        {/* Panel 1: agency + hero */}
        <View style={styles.panelTop}>
          <View style={styles.agencyBar}>
            {agent.logo_url ? <Image src={agent.logo_url} style={styles.logo} /> : <View />}
            <Text style={styles.agencyName}>{c.agencyNameShort || agent.agency_name}</Text>
          </View>
          {p.photo_url_full ? (
            <Image src={p.photo_url_full} style={styles.hero} />
          ) : (
            <View style={styles.heroNoImg}>
              <Text style={styles.noImgEyebrow}>{p.listing_property_type || "Property"}</Text>
              <Text style={styles.noImgStreet}>{street}</Text>
              <Text style={styles.noImgSub}>{sub}</Text>
            </View>
          )}
        </View>

        {/* fold crease 1 */}
        <View style={styles.foldGuide} />

        {/* Panel 2: address + QR (QR sits in the middle of this 99mm panel
            so it never touches either crease) */}
        <View style={styles.panelMid}>
          <View style={styles.addressStrip}>
            <Text style={styles.street}>{street}</Text>
            <Text style={styles.suburb}>{sub}</Text>
            {showFeatures && (
              <Text style={styles.feat}>{p.bedrooms || 0} Bed · {p.bathrooms || 0} Bath · {p.carspaces || 0} Car</Text>
            )}
          </View>
          <View style={styles.qrWrap}>
            {p.qr_data_url && <Image src={p.qr_data_url} style={styles.qr} />}
          </View>
          <Text style={styles.scanPrompt}>{c.scanPrompt}</Text>
          <Text style={styles.headline}>{c.headline}</Text>
        </View>

        {/* fold crease 2 */}
        <View style={styles.foldGuide} />

        {/* Panel 3: agent */}
        <View style={styles.panelBot}>
          {agent.profile_image_url ? <Image src={agent.profile_image_url} style={styles.avatar} /> : <View style={styles.avatar} />}
          <View style={{ flexShrink: 1 }}>
            <Text style={styles.name}>{agent.agent_name}</Text>
            <Text style={styles.title}>{agent.agent_title}</Text>
            {agent.agent_phone ? (
              <View style={styles.contactRow}>
                <Text style={styles.contactLabel}>Call</Text>
                <Text style={styles.contactValue}>{agent.agent_phone}</Text>
              </View>
            ) : null}
            {agent.agent_email ? (
              <View style={styles.contactRow}>
                <Text style={styles.contactLabel}>Email</Text>
                <Text style={styles.contactValue}>{agent.agent_email}</Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </Page>
  );
}

const DESIGN_RENDERERS = {
  classic: ClassicPage,
  bold: BoldPage,
  minimal: MinimalPage,
  magazine: MagazinePage,
  postcard: PostcardPage,
  trifold: TrifoldPage,
};

export default function PropertyQrDocument({ agent, properties, copy, design, agentFontFamily }) {
  const colour = agent.agent_colour || "#2563eb";
  const colourDark = darken(colour, 0.7);
  const bannerText = pickBannerText(colour, agent.agent_colour_lightness);
  const c = { ...DEFAULT_PROPERTY_QR_COPY, ...(copy || {}) };
  const firstName = (agent.agent_name || "your agent").split(" ")[0];
  const inPersonCta = (c.inPersonCta || "")
    .replace("{name}", firstName)
    .replace("{phone}", agent.agent_phone || "");
  const PageComp = DESIGN_RENDERERS[design] || DESIGN_RENDERERS.classic;

  return (
    <Document>
      {properties.map((p) => (
        <PageComp
          key={p.obfuscated_id}
          p={p}
          agent={agent}
          c={c}
          colour={colour}
          colourDark={colourDark}
          bannerText={bannerText}
          inPersonCta={inPersonCta}
          agentFontFamily={agentFontFamily}
        />
      ))}
    </Document>
  );
}
